"use client";

import { ArrowRight, CloudRain, Droplets, Smartphone, ChartBar as BarChart3 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SafetyFirstStandards() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: statRef,
    isVisible: statVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const features = [{
    icon: CloudRain,
    title: "Surgical Precision Cuts",
    description: "Our arborists utilize technical rigging and surgical cutting techniques to protect your property."
  }, {
    icon: Droplets,
    title: "Deep Root Fertilization",
    description: "Targeted nutrient delivery directly to the root zone for resilient, healthy DFW canopies."
  }, {
    icon: Smartphone,
    title: "Digital Health Tracking",
    description: "Monitor your landscape's health and service history through our client portal."
  }, {
    icon: BarChart3,
    title: "Risk Assessment Data",
    description: "Comprehensive structural analysis to identify potential hazards before they become emergencies."
  }];
  return <section data-section-id="278" id="safety-first-standards" className="dark bg-background text-foreground relative overflow-hidden py-20 md:py-28 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(ellipse at top, var(--primary) 0%, transparent 50%), radial-gradient(ellipse at bottom right, var(--primary) 0%, transparent 60%)"
    }} />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {}
          <div ref={imageRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative rounded-lg overflow-hidden shadow-2xl aspect-[4/5]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5147.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5147.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/IMG_5147.jpg-wvc-sizes" alt="Professional arborist performing a precision cut on a large oak tree" loading="lazy" decoding="async" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div ref={headerRef} className={`transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p className="font-mono text-xs sm:text-sm tracking-[0.2em] uppercase text-primary mb-6">
                02 // Technical Standards
              </p>
              <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground mb-6 tracking-tighter">
                Arboriculture,{" "}
                <em className="italic font-light text-primary">engineered.</em>
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed max-w-2xl mb-10">
                Arbor Pro combines ISA-certified expertise with advanced rigging technology to provide the most precise tree care in Fort Worth. We don't just trim; we architect your canopy for long-term structural integrity.
              </p>
            </div>

            {}
            <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 gap-px bg-border/20 rounded-lg overflow-hidden mb-12 transition-all duration-1000 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              {features.map((feature, i) => {
              const Icon = feature.icon;
              return <div key={i} data-index={i} className="bg-card p-6 sm:p-7 flex flex-col gap-3 border border-border/10">
                    <Icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                    <h3 className="font-default font-semibold text-lg sm:text-xl text-foreground mt-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-default leading-relaxed">
                      {feature.description}
                    </p>
                  </div>;
            })}
            </div>

            {}
            <div ref={statRef} className={`flex flex-col sm:flex-row items-start sm:items-end gap-4 sm:gap-6 mb-10 transition-all duration-1000 ease-out ${statVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <span className="font-default font-extrabold text-7xl sm:text-8xl leading-none text-primary tracking-tighter">
                100%
              </span>
              <div className="flex-1 pb-2">
                <Separator className="mb-3 bg-primary/30" />
                <p className="font-default font-semibold text-xl sm:text-2xl text-foreground">
                  Safety Compliance Rate
                </p>
                <p className="text-sm text-muted-foreground font-mono mt-1">
                  Zero-incident record across all residential DFW projects.
                </p>
              </div>
            </div>

            {}
            <Button variant="outline" size="lg" className="border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground rounded-lg px-8 group font-mono uppercase tracking-wider" asChild>
              <Link to="/OurExpertisePage">
                View Technical Specs
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}